'use client';

import { shouldShowDealFab } from '@/lib/dealFabRoutes';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

const DEAL_ROOT_ID = 'hzc-deal-root';
const SHOW_DEAL_CLASS = 'hzc-show-deal';

/**
 * GTM deal widget: yalnızca anasayfa + PLP'de göster; mobilde konumlandır.
 */
export default function ProductDealBadge() {
  const pathname = usePathname();

  useEffect(() => {
    const show = shouldShowDealFab(pathname);
    document.documentElement.classList.toggle(SHOW_DEAL_CLASS, show);
    return () => document.documentElement.classList.remove(SHOW_DEAL_CLASS);
  }, [pathname]);

  useEffect(() => {
    if (!shouldShowDealFab(pathname)) return;

    let el: HTMLElement | null = null;
    let observer: MutationObserver | null = null;
    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let stopTimeoutId: ReturnType<typeof setTimeout> | undefined;

    const isMobile = () => window.matchMedia('(max-width: 639px)').matches;

    const applyStyles = (target: HTMLElement) => {
      el = target;
      if (!isMobile()) return;

      Object.assign(target.style, {
        position: 'fixed',
        bottom: 'calc(5.5rem + env(safe-area-inset-bottom, 0px))',
        zIndex: '51',
      });
    };

    const tryApply = () => {
      const existing = document.getElementById(DEAL_ROOT_ID);
      if (!existing) return false;
      applyStyles(existing);
      observer?.disconnect();
      observer = null;
      return true;
    };

    const watch = () => {
      if (tryApply()) return;
      observer = new MutationObserver(() => {
        tryApply();
      });
      observer.observe(document.documentElement, { childList: true, subtree: true });
      stopTimeoutId = setTimeout(() => {
        observer?.disconnect();
        observer = null;
      }, 15000);
    };

    // Defer until idle so restyle does not compete with LCP/CLS window.
    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(() => watch(), { timeout: 5000 });
    } else {
      timeoutId = setTimeout(watch, 3000);
    }

    return () => {
      if (idleId !== undefined && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId) clearTimeout(timeoutId);
      if (stopTimeoutId) clearTimeout(stopTimeoutId);
      observer?.disconnect();
      if (el) {
        el.style.position = '';
        el.style.left = '';
        el.style.bottom = '';
        el.style.zIndex = '';
        el.style.transform = '';
        el.style.transition = '';
      }
    };
  }, [pathname]);

  return null;
}
